import React, { useState, useEffect, useRef } from 'react';
import {
  Calendar, Users, MessageSquare, Trophy, DollarSign,
  Shield, BarChart3, Plus, Lock, LayoutGrid, Kanban,
} from 'lucide-react';
import ProgramRosterBoard from './ProgramRosterBoard';
import SquadCard from '../../../squads/components/SquadCard';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useFeatureFlags } from '../../../contexts/FeatureFlagsContext';
import { useSquads } from '../../../squads/hooks/useSquads';
import SquadDetailsProvider from '../../../squads/services/SquadDetailsProvider';
import SquadSummaryHandler from '../../../squads/components/SquadSummaryHandler';
import SquadCreateEdit from '../../../squads/components/SquadCreateEdit';
import PostsTab from '../../../squads/tabs/PostsTab';
import MembersTab from '../../../squads/tabs/MembersTab';
import ChatTab from '../../../squads/tabs/ChatTab';
import ScheduleTab from '../../../squads/tabs/ScheduleTab';
import CurrentEventTab from '../../../squads/tabs/CurrentEventTab';
import PastGamesTab from '../../../squads/tabs/PastGamesTab';
import ExpenseTab from '../../../squads/tabs/ExpenseTab';
import TeamsTab from '../../../squads/tabs/TeamsTab';
import StatsTab from '../../../squads/tabs/StatsTab';
import Tabs from '../../../components/common/Tabs';
import { academyAPI } from '../../../services/api';
import toast from 'react-hot-toast';

// ── Inline squad view ──────────────────────────────────────────────────────────
function InlineSquadView({ squadId, hideTabs = [], hideTournaments = false }) {
  const { theme } = useTheme();
  const { isAuthenticated } = useAuth();
  const { displayGroupFeeds } = useFeatureFlags();
  const { getGroup, getGroupMembers } = useSquads(false);

  const voProviderRef = useRef(null);
  const [ready, setReady]   = useState(false);
  const [error, setError]   = useState(null);
  const [activeTab, setActiveTab] = useState('members');
  const [chatUnread, setChatUnread]   = useState(0);
  const [dynamicGameTab, setDynamicGameTab] = useState(null);

  useEffect(() => {
    setReady(false);
    setError(null);
    setActiveTab('members');
    const init = async () => {
      try {
        voProviderRef.current = new SquadDetailsProvider(squadId);
        await voProviderRef.current.initialize(new URLSearchParams(), { getGroup, getGroupMembers });
        setReady(true);
      } catch (err) {
        setError(err.message || 'Failed to load squad');
      }
    };
    if (squadId && isAuthenticated) init();
  }, [squadId, isAuthenticated]);

  if (!ready && !error) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-[3px] border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <Shield size={36} className={`mx-auto mb-3 ${theme.textMuted} opacity-30`} />
        <p className={`text-sm ${theme.textMuted}`}>{error}</p>
      </div>
    );
  }

  const squad     = voProviderRef.current?.getGroup();
  const sportName = squad?.sportType?.sportName?.toLowerCase();

  const tabs = [
    ...(displayGroupFeeds ? [{ id: 'posts', label: 'Posts', icon: MessageSquare }] : []),
    { id: 'members',       label: 'Members',       icon: Users },
    { id: 'chat',          label: 'Chat',          icon: MessageSquare, badge: chatUnread > 0 ? chatUnread : null },
    { id: 'schedule',      label: 'Schedule',      icon: Calendar },
    { id: 'current-event', label: 'Current Event', shortLabel: 'Event', icon: Trophy },
    { id: 'past-games',    label: 'Past Games',    shortLabel: 'Games', icon: Trophy },
    { id: 'expenses',      label: 'Expenses',      icon: DollarSign },
    { id: 'statistics',    label: 'Statistics',    shortLabel: 'Stats', icon: BarChart3 },
    ...(sportName !== 'poker' ? [{ id: 'teams', label: 'Teams', icon: Shield }] : []),
    ...(dynamicGameTab ? [{
      id: 'game-view', label: dynamicGameTab.label, shortLabel: 'Game', icon: Trophy,
      onClose: () => { setDynamicGameTab(null); setActiveTab('past-games'); },
    }] : []),
  ].filter(tab => !hideTabs.includes(tab.id));

  if (!squad?.isMember) {
    return (
      <div className={`${theme.card} rounded-xl border ${theme.border} p-16 text-center`}>
        <Lock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className={`text-lg font-semibold ${theme.text} mb-2`}>Members Only</h3>
        <p className={`${theme.textMuted} text-sm`}>
          You need to be a member of this squad to view its content.
        </p>
      </div>
    );
  }

  return (
    <div>
      <SquadSummaryHandler voProvider={voProviderRef.current} hideTournaments={hideTournaments} />
      <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab}
        mobileVisibleTabs={['members', 'current-event', 'chat']} className="mt-4 mb-6" />
      {displayGroupFeeds && activeTab === 'posts' && (
        <PostsTab voProvider={voProviderRef.current} />
      )}
      {activeTab === 'members'       && <MembersTab voProvider={voProviderRef.current} />}
      {activeTab === 'chat'          && <ChatTab voProvider={voProviderRef.current} />}
      {activeTab === 'schedule'      && <ScheduleTab voProvider={voProviderRef.current} />}
      {activeTab === 'current-event' && (
        <CurrentEventTab voProvider={voProviderRef.current} />
      )}
      {activeTab === 'past-games' && (
        <PastGamesTab voProvider={voProviderRef.current} />
      )}
      {activeTab === 'expenses'   && <ExpenseTab voProvider={voProviderRef.current} />}
      {activeTab === 'statistics' && <StatsTab voProvider={voProviderRef.current} />}
      {activeTab === 'teams' && sportName !== 'poker' && <TeamsTab voProvider={voProviderRef.current} />}
    </div>
  );
}

// ── Map API squad shape → SquadCard shape ──────────────────────────────────────
function toSquadCardShape(squad) {
  return {
    id:           squad.groupId,
    name:         squad.name,
    description:  squad.description,
    backgroundImage: squad.backgroundImage,
    memberCount:  squad.memberCount,
    postCount:    0,
    sportTypeId:  null,
    sportName:    squad.sportName,
    creatorUsername: squad.creatorName,
    createdAt:    squad.linkedAt,
    privacy:      'public',
    isMember:     true,
    memberStatus: 'active',
    userRole:     null,
  };
}

// ── Main panel ─────────────────────────────────────────────────────────────────
export default function ProgramSquadsPanel({ program, academyId, userRole, openSquad, onSquadOpen, onSquadClose }) {
  const { theme } = useTheme();
  const [squads, setSquads]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [linking, setLinking]     = useState(false);
  const [boardView, setBoardView] = useState('squads'); // 'squads' | 'board'

  const isAdminOrCoach = userRole === 'admin' || userRole === 'owner' || userRole === 'coach';

  useEffect(() => {
    if (!academyId || !program?.id) return;
    setLoading(true);
    academyAPI.squads.list(academyId, program.id)
      .then(data => setSquads(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Failed to load squads'))
      .finally(() => setLoading(false));
  }, [academyId, program?.id]);

  const handleSquadCreated = async (newSquad) => {
    // newSquad.id is the group UUID returned by SquadCreateEdit
    setLinking(true);
    try {
      await academyAPI.squads.add(academyId, program.id, newSquad.id);
      // Refresh list so the new squad appears with full group data
      const fresh = await academyAPI.squads.list(academyId, program.id);
      setSquads(Array.isArray(fresh) ? fresh : []);
      toast.success(`${newSquad.name} linked to ${program.name}`);
    } catch (err) {
      toast.error(err?.message || 'Squad created but could not be linked to this program');
    } finally {
      setLinking(false);
    }
  };

  const handleRemove = async (squad) => {
    try {
      await academyAPI.squads.remove(academyId, program.id, squad.groupId);
      setSquads(prev => prev.filter(s => s.groupId !== squad.groupId));
      toast.success(`${squad.name} removed from program`);
    } catch (err) {
      toast.error(err?.message || 'Failed to remove squad');
    }
  };

  // Squad is open — render inline at full width
  if (openSquad) {
    return (
      <InlineSquadView
        key={openSquad.groupId}
        squadId={openSquad.groupId}
        hideTabs={['expenses']}
        hideTournaments
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-7 h-7 border-[3px] border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (squads.length === 0) {
    return (
      <>
        <div className={`text-center py-16 ${theme.card} border ${theme.border} rounded-xl`}>
          <Shield size={40} className={`mx-auto mb-3 ${theme.textMuted} opacity-30`} />
          <p className={`font-semibold ${theme.text}`}>No squads yet</p>
          <p className={`text-sm ${theme.textMuted} mt-1 mb-5`}>
            {isAdminOrCoach
              ? 'Divide enrolled students into focused training groups.'
              : 'Squads will appear here once created.'}
          </p>
          {isAdminOrCoach && (
            <button onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition">
              <Plus size={15} /> Create First Squad
            </button>
          )}
        </div>
        <SquadCreateEdit isOpen={showCreate} onClose={() => setShowCreate(false)}
          onSuccess={handleSquadCreated} mode="create" fixedSportTypeId={program?.sportTypeId} />
      </>
    );
  }

  return (
    <>
      {/* ── Header bar ── */}
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h3 className={`font-semibold ${theme.text}`}>Squads</h3>
          <p className={`text-sm ${theme.textSecondary} mt-0.5`}>
            {squads.length} squad{squads.length !== 1 ? 's' : ''} in this program
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* View toggle — only admins/coaches can use Roster Board */}
          {isAdminOrCoach && (
            <div className={`flex items-center rounded-lg border ${theme.border} overflow-hidden`}>
              <button
                onClick={() => setBoardView('squads')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition ${
                  boardView === 'squads'
                    ? 'bg-blue-500 text-white'
                    : `${theme.text} ${theme.cardHover}`
                }`}
              >
                <LayoutGrid size={13} /> Squads
              </button>
              <button
                onClick={() => setBoardView('board')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition ${
                  boardView === 'board'
                    ? 'bg-blue-500 text-white'
                    : `${theme.text} ${theme.cardHover}`
                }`}
              >
                <Kanban size={13} /> Roster Board
              </button>
            </div>
          )}

          {isAdminOrCoach && boardView === 'squads' && (
            <button onClick={() => setShowCreate(true)}
              className="flex items-center gap-1.5 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition"
              disabled={linking}>
              <Plus size={15} /> New Squad
            </button>
          )}
        </div>
      </div>

      {/* ── Squads grid ── */}
      {boardView === 'squads' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {squads.map(squad => (
            <SquadCard
              key={squad.groupId}
              squad={toSquadCardShape(squad)}
              onView={() => onSquadOpen(squad)}
              onRemove={isAdminOrCoach ? () => handleRemove(squad) : undefined}
              hideLeave
            />
          ))}
        </div>
      )}

      {/* ── Roster board ── */}
      {boardView === 'board' && (
        <ProgramRosterBoard program={program} academyId={academyId} />
      )}

      <SquadCreateEdit isOpen={showCreate} onClose={() => setShowCreate(false)}
        onSuccess={handleSquadCreated} mode="create" fixedSportTypeId={program?.sportTypeId} />
    </>
  );
}
